import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface MatchRequestRow {
  id: string;
  created_at: string;
  expire_at: string;
  status: string;
  created_by_email: string | null;
  student_code: string | null;
  class_trial_id: string | null;
  student_display_name: string | null;
  search_filters: Record<string, any> | null;
  message_preview: string | null;
  student_label: string;
  counts: { total: number; sent: number; failed: number; pending: number; confirmed: number; denied: number };
}

export interface MatchRequestTeacherRow {
  id: string;
  teacher_code: string;
  status: 'pending' | 'confirmed' | 'denied' | string;
  deny_reason: string | null;
  line_send_status: string | null;
  line_error: string | null;
  responded_at: string | null;
  created_at: string;
  teacher_name: string | null;
  teacher_nickname: string | null;
  academic_achievements: string | null;
  teaching_experience: string | null;
  profile_picture: string | null;
}

const fs = () => (supabase as any).schema('fishsix');

export function useMatchHistoryList(params: { search?: string; status?: string; limit?: number } = {}) {
  const { search = '', status = 'all', limit = 50 } = params;
  return useQuery<MatchRequestRow[]>({
    queryKey: ['match-history-list', { search, status, limit }],
    queryFn: async () => {
      let q = fs()
        .from('teacher_matching_requests')
        .select('id, created_at, expire_at, status, created_by_email, student_code, class_trial_id, student_display_name, search_filters, message_preview')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (status !== 'all') q = q.eq('status', status);
      if (search.trim()) {
        const s = `%${search.trim()}%`;
        q = q.or(`student_display_name.ilike.${s},student_code.ilike.${s},created_by_email.ilike.${s}`);
      }
      const { data: reqs, error } = await q;
      if (error) throw error;
      const rows = (reqs || []) as any[];
      if (rows.length === 0) return [];

      const ids = rows.map(r => r.id);
      const { data: tRows, error: tErr } = await fs()
        .from('matching_request_teachers')
        .select('request_id, status, line_send_status')
        .in('request_id', ids);
      if (tErr) throw tErr;

      // Resolve student labels for student_code rows
      const codes = Array.from(new Set(rows.map(r => r.student_code).filter(Boolean)));
      const trialIds = Array.from(new Set(rows.map(r => r.class_trial_id).filter(Boolean)));

      const [studentsRes, trialsRes] = await Promise.all([
        codes.length
          ? (supabase as any).schema('student').from('fishsix_students').select('code, first_name, nickname').in('code', codes as string[])
          : Promise.resolve({ data: [], error: null } as any),
        trialIds.length
          ? supabase.from('class_trials').select('id, student_firstname, student_nickname, student_name').in('id', trialIds as string[])
          : Promise.resolve({ data: [], error: null } as any),
      ]);
      const studentByCode = new Map<string, any>();
      (studentsRes.data || []).forEach((s: any) => studentByCode.set(s.code, s));
      const trialById = new Map<string, any>();
      (trialsRes.data || []).forEach((t: any) => trialById.set(t.id, t));

      const countMap = new Map<string, MatchRequestRow['counts']>();
      (tRows || []).forEach((r: any) => {
        const c = countMap.get(r.request_id) || { total: 0, sent: 0, failed: 0, pending: 0, confirmed: 0, denied: 0 };
        c.total += 1;
        if (r.line_send_status === 'sent') c.sent += 1;
        if (r.line_send_status === 'failed') c.failed += 1;
        if (r.status === 'pending') c.pending += 1;
        if (r.status === 'confirmed') c.confirmed += 1;
        if (r.status === 'denied') c.denied += 1;
        countMap.set(r.request_id, c);
      });

      return rows.map(r => {
        let label = '-';
        if (r.student_code) {
          const s = studentByCode.get(r.student_code);
          label = s ? `${s.first_name || ''}${s.nickname ? ` (${s.nickname})` : ''} — ${r.student_code}` : r.student_code;
        } else if (r.class_trial_id) {
          const t = trialById.get(r.class_trial_id);
          const fn = t?.student_firstname || t?.student_name || 'ทดลองเรียน';
          label = `${fn}${t?.student_nickname ? ` (${t.student_nickname})` : ''} — ทดลองเรียน`;
        } else if (r.student_display_name) {
          label = `${r.student_display_name} — พิมพ์เอง`;
        }
        return {
          ...r,
          student_label: label,
          counts: countMap.get(r.id) || { total: 0, sent: 0, failed: 0, pending: 0, confirmed: 0, denied: 0 },
        } as MatchRequestRow;
      });
    },
  });
}

export function useMatchHistoryDetail(requestId: string | undefined) {
  return useQuery<{ request: MatchRequestRow; teachers: MatchRequestTeacherRow[] }>({
    queryKey: ['match-history-detail', requestId],
    enabled: !!requestId,
    queryFn: async () => {
      const { data: req, error } = await fs()
        .from('teacher_matching_requests')
        .select('*')
        .eq('id', requestId)
        .single();
      if (error) throw error;

      const { data: teachers, error: tErr } = await fs()
        .from('matching_request_teachers')
        .select('id, teacher_code, status, deny_reason, line_send_status, line_error, responded_at, created_at')
        .eq('request_id', requestId)
        .order('created_at', { ascending: true });
      if (tErr) throw tErr;

      const codes = (teachers || []).map((t: any) => t.teacher_code);
      const { data: profiles } = codes.length
        ? await supabase.from('teacher_profiles').select('teacher_code, full_name, nickname, academic_achievements, teaching_experience, profile_picture').in('teacher_code', codes)
        : { data: [] as any[] };
      const byCode = new Map<string, any>();
      (profiles || []).forEach((p: any) => byCode.set(p.teacher_code, p));

      // Resolve student label
      let label = '-';
      if (req.student_code) {
        const { data: s } = await (supabase as any).schema('student').from('fishsix_students').select('first_name, nickname').eq('code', req.student_code).maybeSingle();
        label = s ? `${s.first_name || ''}${s.nickname ? ` (${s.nickname})` : ''} — ${req.student_code}` : req.student_code;
      } else if (req.class_trial_id) {
        const { data: t } = await supabase.from('class_trials').select('student_firstname, student_nickname, student_name').eq('id', req.class_trial_id).maybeSingle();
        const fn = t?.student_firstname || t?.student_name || 'ทดลองเรียน';
        label = `${fn}${t?.student_nickname ? ` (${t.student_nickname})` : ''} — ทดลองเรียน`;
      } else if (req.student_display_name) {
        label = `${req.student_display_name} — พิมพ์เอง`;
      }

      const tRows: MatchRequestTeacherRow[] = (teachers || []).map((t: any) => ({
        ...t,
        teacher_name: byCode.get(t.teacher_code)?.full_name || null,
        teacher_nickname: byCode.get(t.teacher_code)?.nickname || null,
        academic_achievements: byCode.get(t.teacher_code)?.academic_achievements || null,
        teaching_experience: byCode.get(t.teacher_code)?.teaching_experience || null,
        profile_picture: byCode.get(t.teacher_code)?.profile_picture || null,
      }));

      const counts = { total: tRows.length, sent: 0, failed: 0, pending: 0, confirmed: 0, denied: 0 };
      tRows.forEach(r => {
        if (r.line_send_status === 'sent') counts.sent += 1;
        if (r.line_send_status === 'failed') counts.failed += 1;
        if (r.status === 'pending') counts.pending += 1;
        if (r.status === 'confirmed') counts.confirmed += 1;
        if (r.status === 'denied') counts.denied += 1;
      });

      return {
        request: { ...(req as any), student_label: label, counts } as MatchRequestRow,
        teachers: tRows,
      };
    },
  });
}

export function useCloseMatchRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await fs()
        .from('teacher_matching_requests')
        .update({ status: 'complete' })
        .eq('id', requestId);
      if (error) throw error;
      return true;
    },
    onSuccess: (_d, requestId) => {
      qc.invalidateQueries({ queryKey: ['match-history-detail', requestId] });
      qc.invalidateQueries({ queryKey: ['match-history-list'] });
    },
  });
}
